import React from "react";
import QRCode from "react-qr-code";
import {
  FONT_FAMILIES,
  LINE_HEIGHT,
  code39Bars,
  elementText,
  fittedFontMm,
} from "./labelModel";

/** Code 39 as SVG; bars stretch to the element box. */
export function Code39Svg({ value }) {
  const { bars, width } = code39Bars(value);
  if (!bars.length) return null;
  return (
    <svg
      viewBox={`0 0 ${width} 100`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%", display: "block" }}
      aria-hidden="true"
    >
      {bars.map((b, i) => (
        <rect key={i} x={b.x} y={0} width={b.w} height={100} fill="#000" />
      ))}
    </svg>
  );
}

/**
 * Inner content of one element at `s` px per mm. Shared by the editor canvas
 * and the print document so the printout matches the preview.
 */
export function LabelElementContent({ el, record, s }) {
  const value = elementText(el, record);

  if (el.type === "text") {
    const fontMm = fittedFontMm(el, value);
    return (
      <div
        style={{
          fontFamily: FONT_FAMILIES[el.font] || FONT_FAMILIES.sans,
          fontWeight: el.weight,
          fontSize: fontMm * s,
          lineHeight: LINE_HEIGHT,
          textAlign: el.align,
          whiteSpace: el.fit ? "pre" : "pre-wrap",
          overflow: "hidden",
          width: "100%",
          maxHeight: "100%",
          color: "#000",
          letterSpacing: el.font === "cond" ? "-0.01em" : 0,
        }}
      >
        {value}
      </div>
    );
  }

  if (el.type === "barcode") {
    const len = Math.max(6, value.length * 0.7);
    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <Code39Svg value={value} />
        </div>
        {el.showText && (
          <div
            style={{
              fontFamily: FONT_FAMILIES.mono,
              fontSize: Math.min(el.h * s * 0.2, (el.w * s) / len),
              textAlign: "center",
              color: "#000",
              lineHeight: 1.2,
              paddingTop: 1,
              letterSpacing: "0.08em",
            }}
          >
            {value}
          </div>
        )}
      </div>
    );
  }

  if (el.type === "qr") {
    if (!value) return null;
    const size = Math.min(el.w, el.h) * s;
    return (
      <QRCode
        value={value}
        size={size}
        level="M"
        bgColor="#FFFFFF"
        fgColor="#000000"
        style={{ width: size, height: size, display: "block" }}
      />
    );
  }

  if (el.type === "line") {
    return <div style={{ width: "100%", height: Math.max(1, el.h * s), background: "#000" }} />;
  }
  return null;
}

/** Absolute box style for an element. */
export function elementBoxStyle(el, s) {
  const style = {
    position: "absolute",
    left: el.x * s,
    top: el.y * s,
    width: el.w * s,
    height: el.h * s,
    display: "flex",
    alignItems: "center",
  };
  if (el.type === "text") {
    style.justifyContent =
      el.align === "right" ? "flex-end" : el.align === "center" ? "center" : "flex-start";
  }
  if (el.type === "qr") style.justifyContent = "center";
  return style;
}

/** One finished label (no editor chrome) — used for printing. */
export function LabelPage({ elements, record, W, H, s }) {
  return (
    <div
      className="label-page"
      style={{ position: "relative", width: W * s, height: H * s, overflow: "hidden", background: "#fff" }}
    >
      {elements.map((el) => (
        <div key={el.id} style={elementBoxStyle(el, s)}>
          <LabelElementContent el={el} record={record} s={s} />
        </div>
      ))}
    </div>
  );
}
